import { useState, useEffect } from 'react';
import { reportsAPI } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const fmt   = v => `R$ ${parseFloat(v||0).toFixed(2).replace('.',',')}`;
const COLORS = ['#2563EB','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#EC4899','#84CC16'];

export default function ReportPage() {
  const [activeTab, setActiveTab] = useState('sales');
  const [filters, setFilters]     = useState({ date_from: '', date_to: '' });
  const [data, setData]           = useState({});
  const [loading, setLoading]     = useState(false);

  const defaultDates = () => {
    const now   = new Date();
    const from  = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10);
    const to    = now.toISOString().slice(0,10);
    return { date_from: from, date_to: to };
  };

  useEffect(() => {
    setFilters(defaultDates());
  }, []);

  useEffect(() => {
    if (!filters.date_from) return;
    load();
  }, [activeTab, filters]);

  const load = async () => {
    setLoading(true);
    try {
      let r;
      if (activeTab === 'sales')     r = await reportsAPI.sales(filters);
      if (activeTab === 'services')  r = await reportsAPI.services(filters);
      if (activeTab === 'employees') r = await reportsAPI.employees(filters);
      if (activeTab === 'customers') r = await reportsAPI.customers(filters);
      setData(r.data.data || {});
    } catch { setData({}); }
    finally { setLoading(false); }
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const TABS = [['sales','Vendas'],['services','Serviços'],['employees','Colaboradores'],['customers','Clientes']];

  return (
    <div>
      <h1 style={{ fontSize:'1.5rem', fontWeight:700, color:'#0F172A', marginBottom:24 }}>Relatórios</h1>

      <div style={{ display:'flex', gap:0, borderBottom:'2px solid #E5E7EB', marginBottom:20 }}>
        {TABS.map(([t,l]) => (
          <button key={t} onClick={()=>setActiveTab(t)}
            style={{ padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontWeight:700, fontSize:'.9rem',
              color: activeTab===t?'#2563EB':'#6B7280', borderBottom: activeTab===t?'2px solid #2563EB':'2px solid transparent', marginBottom:-2 }}>
            {l}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div>
          <label style={{ display:'block', fontSize:'.8rem', fontWeight:600, color:'#374151', marginBottom:4 }}>De</label>
          <input type="date" value={filters.date_from} onChange={e=>setF('date_from',e.target.value)}
            style={{ padding:'8px 12px', border:'1.5px solid #E5E7EB', borderRadius:8, outline:'none' }} />
        </div>
        <div>
          <label style={{ display:'block', fontSize:'.8rem', fontWeight:600, color:'#374151', marginBottom:4 }}>Até</label>
          <input type="date" value={filters.date_to} onChange={e=>setF('date_to',e.target.value)}
            style={{ padding:'8px 12px', border:'1.5px solid #E5E7EB', borderRadius:8, outline:'none' }} />
        </div>
        <button onClick={load} style={{ padding:'9px 20px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>Atualizar</button>
      </div>

      {loading && <div style={{ textAlign:'center', padding:40, color:'#6B7280' }}>Carregando...</div>}

      {!loading && activeTab === 'sales' && data.summary && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:14, marginBottom:24 }}>
            {[
              { label:'Total de O.S.', value: data.summary.total_orders, unit:'', color:'#2563EB' },
              { label:'Receita Total', value: fmt(data.summary.total_revenue), unit:'', color:'#059669' },
              { label:'Ticket Médio', value: fmt(data.summary.avg_ticket), unit:'', color:'#7C3AED' },
              { label:'Clientes únicos', value: data.summary.unique_customers, unit:'', color:'#0891B2' },
            ].map(c => (
              <div key={c.label} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:18 }}>
                <div style={{ fontSize:'.8rem', color:'#6B7280', marginBottom:6 }}>{c.label}</div>
                <div style={{ fontSize:'1.4rem', fontWeight:800, color:c.color }}>{c.value}</div>
              </div>
            ))}
          </div>
          {data.by_day?.length > 0 && (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24, marginBottom:16 }}>
              <h3 style={{ fontWeight:700, marginBottom:16 }}>Receita por dia</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.by_day}>
                  <XAxis dataKey="date" tick={{ fontSize:11 }} />
                  <YAxis tick={{ fontSize:11 }} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Bar dataKey="revenue" fill="#2563EB" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {data.by_payment_method?.length > 0 && (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24 }}>
              <h3 style={{ fontWeight:700, marginBottom:16 }}>Por forma de pagamento</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, alignItems:'center' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={data.by_payment_method} dataKey="revenue" nameKey="method" cx="50%" cy="50%" outerRadius={80}>
                      {data.by_payment_method.map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v=>fmt(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <div>
                  {data.by_payment_method.map((m, i) => (
                    <div key={m.method} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #F1F5F9' }}>
                      <span style={{ display:'flex', alignItems:'center', gap:8, fontSize:'.85rem', textTransform:'capitalize' }}>
                        <span style={{ width:10, height:10, borderRadius:'50%', background:COLORS[i%COLORS.length], display:'inline-block' }} />
                        {m.method?.replace(/_/g,' ')}
                      </span>
                      <span style={{ fontWeight:700, color:'#059669' }}>{fmt(m.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && activeTab === 'services' && data.top_services?.length > 0 && (
        <div>
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24, marginBottom:16 }}>
            <h3 style={{ fontWeight:700, marginBottom:16 }}>Serviços mais realizados</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.top_services} layout="vertical">
                <XAxis type="number" tick={{ fontSize:11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} width={120} />
                <Tooltip />
                <Bar dataKey="qty" fill="#2563EB" radius={[0,4,4,0]} name="Qtd" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
                {['Serviço','Qtd','Receita','Ticket Médio'].map(h=>(
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:'.8rem', fontWeight:600, color:'#6B7280' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.top_services.map(s=>(
                  <tr key={s.name} style={{ borderBottom:'1px solid #F1F5F9' }}>
                    <td style={{ padding:'12px 16px', fontWeight:600 }}>{s.name}</td>
                    <td style={{ padding:'12px 16px' }}>{s.qty}</td>
                    <td style={{ padding:'12px 16px', color:'#059669', fontWeight:700 }}>{fmt(s.revenue)}</td>
                    <td style={{ padding:'12px 16px', color:'#6B7280' }}>{fmt(s.avg_ticket)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === 'employees' && data.employees?.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
              {['Colaborador','O.S.','Receita','Comissão Pendente'].map(h=>(
                <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:'.8rem', fontWeight:600, color:'#6B7280' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.employees.map(e=>(
                <tr key={e.name} style={{ borderBottom:'1px solid #F1F5F9' }}>
                  <td style={{ padding:'12px 16px', fontWeight:600 }}>{e.name}</td>
                  <td style={{ padding:'12px 16px' }}>{e.orders}</td>
                  <td style={{ padding:'12px 16px', color:'#059669', fontWeight:700 }}>{fmt(e.revenue)}</td>
                  <td style={{ padding:'12px 16px', color:'#D97706', fontWeight:600 }}>{fmt(e.pending_commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && activeTab === 'customers' && data.top_customers?.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
              {['#','Cliente','O.S.','Total Gasto'].map(h=>(
                <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:'.8rem', fontWeight:600, color:'#6B7280' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.top_customers.map((c,i)=>(
                <tr key={c.name} style={{ borderBottom:'1px solid #F1F5F9' }}>
                  <td style={{ padding:'12px 16px', fontWeight:700, color: i===0?'#F59E0B': i===1?'#9CA3AF': i===2?'#D97706':'#374151' }}>
                    {i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}
                  </td>
                  <td style={{ padding:'12px 16px', fontWeight:600 }}>{c.name}</td>
                  <td style={{ padding:'12px 16px' }}>{c.orders}</td>
                  <td style={{ padding:'12px 16px', color:'#059669', fontWeight:800, fontSize:'1rem' }}>{fmt(c.total_spent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && Object.keys(data).length === 0 && (
        <div style={{ textAlign:'center', padding:60, color:'#9CA3AF' }}>Nenhum dado para o período selecionado.</div>
      )}
    </div>
  );
}
